import React, { useState, useRef, useCallback } from 'react';
import { Box, IconButton, InputBase, Typography, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TextPreview from './TextPreview';
import DateTimePreview from './DateTimePreview';
import SelectionPreview from './SelectionPreview';
import DisplayPreview from './DisplayPreview';
import FileUploadPreview from './FileUploadPreview';
import RichTextPreview from './RichTextPreview';
import ListPreview from './ListPreview';
import { getTypeInfo, FIELD_CATEGORIES } from './fieldTypes';
import { inputBaseSx, SettingsInput, SettingsRow, SettingsToggle, SectionHeader, SegmentedPicker } from './SettingsPrimitives';
import { NODE_UI } from '../../../NodeUI/nodeUiStyles';
import { TipTapEditor } from '../../../TipTap';

/* ─── Field preview router ─── */

function FieldPreview({ field, onUpdate, palette, theme, availablePlaceholders }) {
  switch (field.type) {
    case 'text':
    case 'number':
      return <TextPreview field={field} onUpdate={onUpdate} palette={palette} theme={theme} availablePlaceholders={availablePlaceholders} />;
    case 'date':
    case 'time':
      return <DateTimePreview field={field} onUpdate={onUpdate} palette={palette} theme={theme} />;
    case 'dropdown':
    case 'radio':
    case 'checkbox':
      return <SelectionPreview field={field} onUpdate={onUpdate} palette={palette} theme={theme} availablePlaceholders={availablePlaceholders} />;
    case 'display':
      return <DisplayPreview field={field} onUpdate={onUpdate} palette={palette} theme={theme} availablePlaceholders={availablePlaceholders} />;
    case 'file_upload':
      return <FileUploadPreview field={field} palette={palette} />;
    case 'rich_text':
      return <RichTextPreview field={field} onUpdate={onUpdate} palette={palette} theme={theme} availablePlaceholders={availablePlaceholders} />;
    case 'list':
      return <ListPreview field={field} onUpdate={onUpdate} palette={palette} theme={theme} />;
    default:
      return null;
  }
}

/* ─── File upload settings ─── */

const COMMON_MIME_GROUPS = [
  { label: 'PDF', mimes: ['application/pdf'] },
  { label: 'Images', mimes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'] },
  { label: 'Word', mimes: ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'] },
  { label: 'Excel', mimes: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] },
  { label: 'CSV', mimes: ['text/csv'] },
  { label: 'Video', mimes: ['video/mp4', 'video/webm', 'video/ogg'] },
  { label: 'Audio', mimes: ['audio/mpeg', 'audio/ogg', 'audio/wav'] },
  { label: 'ZIP', mimes: ['application/zip', 'application/x-zip-compressed'] },
];

function FileUploadSettings({ field, onUpdate, palette, theme }) {
  const upload = field.upload || {};
  const allowedMimes = upload.allowedMimeTypes || [];
  const maxFiles = upload.maxFiles || '';

  const updateUpload = (patch) => onUpdate({ upload: { ...upload, ...patch } });

  const isGroupSelected = (group) => group.mimes.every((m) => allowedMimes.includes(m));

  const toggleGroup = (group) => {
    const selected = isGroupSelected(group);
    let next;
    if (selected) {
      next = allowedMimes.filter((m) => !group.mimes.includes(m));
    } else {
      const toAdd = group.mimes.filter((m) => !allowedMimes.includes(m));
      next = [...allowedMimes, ...toAdd];
    }
    updateUpload({ allowedMimeTypes: next.length ? next : undefined });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <SectionHeader label="File upload" palette={palette} />
      <SettingsInput
        label="Max files"
        value={maxFiles}
        onChange={(v) => updateUpload({ maxFiles: v === '' ? undefined : v })}
        placeholder="Unlimited"
        type="number"
        palette={palette}
        theme={theme}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <Typography sx={{ fontSize: NODE_UI.fontSize.caption, fontWeight: NODE_UI.fontWeight.label, color: palette.textSecondary, userSelect: 'none' }}>
          Allowed file types {allowedMimes.length === 0 && <span style={{ fontWeight: 400, opacity: 0.7 }}>(all types)</span>}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: '2px' }}>
          {COMMON_MIME_GROUPS.map((group) => {
            const active = isGroupSelected(group);
            return (
              <Box
                key={group.label}
                onClick={(e) => { e.stopPropagation(); toggleGroup(group); }}
                sx={{
                  px: '8px',
                  py: '3px',
                  borderRadius: NODE_UI.borderRadiusSm,
                  border: `1px solid ${active ? palette.textSecondary : palette.border}`,
                  backgroundColor: active ? palette.surface : 'transparent',
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.12s ease',
                  '&:hover': { borderColor: palette.textSecondary },
                }}
              >
                <Typography sx={{ fontSize: '11px', fontWeight: active ? 600 : 400, color: active ? palette.textPrimary : palette.textSecondary }}>
                  {group.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

/* ─── Typed constraints per field type ─── */

function TypedConstraintsEditor({ field, validations, onValidationsChange, palette, theme }) {
  const typed = validations.typed || {};
  const updateTyped = (patch) => onValidationsChange({ ...validations, typed: { ...typed, ...patch } });

  if (field.type === 'text') {
    return (
      <SettingsRow>
        <SettingsInput
          label="Min length"
          value={typed.minLength ?? ''}
          onChange={(v) => updateTyped({ minLength: v === '' ? undefined : v })}
          placeholder="—"
          type="number"
          palette={palette}
          theme={theme}
        />
        <SettingsInput
          label="Max length"
          value={typed.maxLength ?? ''}
          onChange={(v) => updateTyped({ maxLength: v === '' ? undefined : v })}
          placeholder="—"
          type="number"
          palette={palette}
          theme={theme}
        />
      </SettingsRow>
    );
  }

  if (field.type === 'number') {
    return (
      <SettingsRow>
        <SettingsInput
          label="Min value"
          value={typed.min ?? ''}
          onChange={(v) => updateTyped({ min: v === '' ? undefined : v })}
          placeholder="—"
          type="number"
          palette={palette}
          theme={theme}
        />
        <SettingsInput
          label="Max value"
          value={typed.max ?? ''}
          onChange={(v) => updateTyped({ max: v === '' ? undefined : v })}
          placeholder="—"
          type="number"
          palette={palette}
          theme={theme}
        />
      </SettingsRow>
    );
  }

  if (field.type === 'date') {
    return (
      <SettingsRow>
        <SettingsInput
          label="Min date"
          value={typed.minDate ?? ''}
          onChange={(v) => updateTyped({ minDate: v || undefined })}
          placeholder="YYYY-MM-DD"
          palette={palette}
          theme={theme}
        />
        <SettingsInput
          label="Max date"
          value={typed.maxDate ?? ''}
          onChange={(v) => updateTyped({ maxDate: v || undefined })}
          placeholder="YYYY-MM-DD"
          palette={palette}
          theme={theme}
        />
      </SettingsRow>
    );
  }

  if (field.type === 'time') {
    return (
      <SettingsRow>
        <SettingsInput
          label="Min time"
          value={typed.minTime ?? ''}
          onChange={(v) => updateTyped({ minTime: v || undefined })}
          placeholder="HH:mm"
          palette={palette}
          theme={theme}
        />
        <SettingsInput
          label="Max time"
          value={typed.maxTime ?? ''}
          onChange={(v) => updateTyped({ maxTime: v || undefined })}
          placeholder="HH:mm"
          palette={palette}
          theme={theme}
        />
      </SettingsRow>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <SettingsRow>
        <SettingsInput
          label="Min selected"
          value={typed.minSelected ?? ''}
          onChange={(v) => updateTyped({ minSelected: v === '' ? undefined : v })}
          placeholder="—"
          type="number"
          palette={palette}
          theme={theme}
        />
        <SettingsInput
          label="Max selected"
          value={typed.maxSelected ?? ''}
          onChange={(v) => updateTyped({ maxSelected: v === '' ? undefined : v })}
          placeholder="—"
          type="number"
          palette={palette}
          theme={theme}
        />
      </SettingsRow>
    );
  }

  if (field.type === 'list') {
    return (
      <SettingsRow>
        <SettingsInput
          label="Min items"
          value={typed.minItems ?? ''}
          onChange={(v) => updateTyped({ minItems: v === '' ? undefined : v })}
          placeholder="—"
          type="number"
          palette={palette}
          theme={theme}
        />
        <SettingsInput
          label="Max items"
          value={typed.maxItems ?? ''}
          onChange={(v) => updateTyped({ maxItems: v === '' ? undefined : v })}
          placeholder="—"
          type="number"
          palette={palette}
          theme={theme}
        />
      </SettingsRow>
    );
  }

  return null;
}

/* ─── Regex validation editor ─── */

function RegexEditor({ validations, onValidationsChange, palette, theme }) {
  const regex = validations.regex || {};
  const updateRegex = (patch) => onValidationsChange({ ...validations, regex: { ...regex, ...patch } });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <SettingsRow>
        <SettingsInput
          label="Pattern"
          value={regex.pattern ?? ''}
          onChange={(v) => updateRegex({ pattern: v })}
          placeholder="^[A-Z].*"
          mono
          palette={palette}
          theme={theme}
        />
        <Box sx={{ maxWidth: 80 }}>
          <SettingsInput
            label="Flags"
            value={regex.flags ?? ''}
            onChange={(v) => updateRegex({ flags: v })}
            placeholder="i"
            mono
            palette={palette}
            theme={theme}
          />
        </Box>
      </SettingsRow>
      <SettingsInput
        label="Error message"
        value={regex.errorMessage ?? ''}
        onChange={(v) => updateRegex({ errorMessage: v })}
        placeholder="Input does not match required format"
        palette={palette}
        theme={theme}
      />
    </Box>
  );
}

/* ─── NL requirement editor ─── */

function RequirementEditor({ validations, onValidationsChange, palette, theme }) {
  const req = validations.requirement || {};
  const updateReq = (patch) => onValidationsChange({ ...validations, requirement: { ...req, ...patch } });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <SettingsInput
        label="Requirement (natural language)"
        value={req.requirementsText ?? ''}
        onChange={(v) => updateReq({ requirementsText: v })}
        placeholder="e.g. Answer must include a company name"
        palette={palette}
        theme={theme}
      />
      <SettingsRow>
        <SegmentedPicker
          label="Mode"
          value={req.evaluationMode || 'ADVISORY'}
          options={[
            { value: 'ADVISORY', label: 'Advisory' },
            { value: 'BLOCKING', label: 'Blocking' },
          ]}
          onChange={(v) => updateReq({ evaluationMode: v })}
          palette={palette}
        />
      </SettingsRow>
      <SettingsInput
        label="Failure message"
        value={req.failureMessage ?? ''}
        onChange={(v) => updateReq({ failureMessage: v })}
        placeholder="Requirement not met."
        palette={palette}
        theme={theme}
      />
    </Box>
  );
}

/* ─── Validation section wrapper ─── */

function ValidationSection({ field, validations, onValidationsChange, palette, theme }) {
  const [expandedSections, setExpandedSections] = useState(() => {
    const init = {};
    if (validations.typed && Object.values(validations.typed).some((v) => v !== undefined && v !== '')) init.typed = true;
    if (validations.regex?.pattern) init.regex = true;
    if (validations.requirement?.requirementsText) init.requirement = true;
    return init;
  });

  const toggle = (key) => setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const hasTypedConstraints = ['text', 'number', 'date', 'time', 'checkbox', 'list'].includes(field.type);
  const isDisplay = field.type === 'display';
  if (isDisplay) return null;

  const sectionBtnSx = (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    px: '8px',
    py: '3px',
    borderRadius: NODE_UI.borderRadiusSm,
    border: `1px solid ${active ? palette.textSecondary : palette.border}`,
    backgroundColor: active ? palette.surface : 'transparent',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'all 0.12s ease',
    '&:hover': { borderColor: palette.textSecondary },
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <SectionHeader label="Validation" palette={palette} />

      {/* Toggle buttons for validation modes */}
      <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        {hasTypedConstraints && (
          <Box onClick={(e) => { e.stopPropagation(); toggle('typed'); }} sx={sectionBtnSx(expandedSections.typed)}>
            <Typography sx={{ fontSize: '11px', fontWeight: expandedSections.typed ? 600 : 400, color: expandedSections.typed ? palette.textPrimary : palette.textSecondary }}>
              Constraints
            </Typography>
          </Box>
        )}
        <Box onClick={(e) => { e.stopPropagation(); toggle('regex'); }} sx={sectionBtnSx(expandedSections.regex)}>
          <Typography sx={{ fontSize: '11px', fontWeight: expandedSections.regex ? 600 : 400, color: expandedSections.regex ? palette.textPrimary : palette.textSecondary }}>
            Regex
          </Typography>
        </Box>
        <Box onClick={(e) => { e.stopPropagation(); toggle('requirement'); }} sx={sectionBtnSx(expandedSections.requirement)}>
          <Typography sx={{ fontSize: '11px', fontWeight: expandedSections.requirement ? 600 : 400, color: expandedSections.requirement ? palette.textPrimary : palette.textSecondary }}>
            AI Requirement
          </Typography>
        </Box>
      </Box>

      {/* Expanded sections */}
      {expandedSections.typed && hasTypedConstraints && (
        <TypedConstraintsEditor
          field={field}
          validations={validations}
          onValidationsChange={onValidationsChange}
          palette={palette}
          theme={theme}
        />
      )}
      {expandedSections.regex && (
        <RegexEditor
          validations={validations}
          onValidationsChange={onValidationsChange}
          palette={palette}
          theme={theme}
        />
      )}
      {expandedSections.requirement && (
        <RequirementEditor
          validations={validations}
          onValidationsChange={onValidationsChange}
          palette={palette}
          theme={theme}
        />
      )}
    </Box>
  );
}

/* ─── Main field block ─── */

export default function WysiwygFieldBlock({
  field,
  index,
  total,
  onUpdate,
  onRemove,
  onMove,
  onDuplicate,
  onAddField,
  theme,
  palette,
  availablePlaceholders,
}) {
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [hovered, setHovered] = useState(false);
  const leaveTimerRef = useRef(null);
  const addMenuOpen = Boolean(addMenuAnchor);
  const isDisplayField = field.type === 'display';

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  const handleFieldUpdate = (updates) => onUpdate(index, updates);

  const handleAddSelect = (type) => {
    onAddField(type, index + 1);
    setAddMenuAnchor(null);
  };

  const handleMouseEnter = useCallback(() => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    setHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    leaveTimerRef.current = setTimeout(() => {
      setHovered(false);
    }, 300);
  }, []);

  const handleValidationsChange = useCallback(
    (newValidations) => onUpdate(index, { validations: newValidations }),
    [index, onUpdate]
  );

  const showControls = hovered || isDragging || settingsExpanded || addMenuOpen;
  const validations = field.validations || {};

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        overflow: 'visible',
        cursor: 'default',
      }}
    >
      {/* Drag handle at top center */}
      <Box
        className="drag-handle"
        {...listeners}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: '1px',
          cursor: 'grab',
          color: palette.textSecondary,
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.15s ease',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon sx={{ width: 16, height: 12, transform: 'rotate(90deg)' }} />
      </Box>

      {/* Field card */}
      <Box
        sx={{
          border: `1px solid ${showControls ? palette.textSecondary : palette.border}`,
          borderRadius: '10px',
          backgroundColor: palette.surface,
          overflow: 'hidden',
          transition: 'border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
          ...(showControls && { boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }),
        }}
      >
        {/* Main content */}
        <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {!isDisplayField && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ flex: 1, minWidth: 0, fontSize: '12px', fontWeight: 600, color: palette.textPrimary }}>
                <TipTapEditor
                  value={field.label || ''}
                  onChange={(val) => handleFieldUpdate({ label: val })}
                  showToolbar={false}
                  stripHtml={true}
                  availablePlaceholders={availablePlaceholders}
                  placeholder="Type your question..."
                  minHeight="auto"
                  className="tiptap-inline"
                />
              </Box>
              {field.required && (
                <Typography sx={{ fontSize: '11px', color: palette.errorText || '#b91c1c', flexShrink: 0 }}>*</Typography>
              )}
            </Box>
          )}

          <FieldPreview
            field={field}
            onUpdate={handleFieldUpdate}
            palette={palette}
            theme={theme}
            availablePlaceholders={availablePlaceholders}
          />

          {!isDisplayField && field.helpText && (
            <Box sx={{ mt: 0.25 }}>
              <Typography sx={{ fontSize: '11px', color: palette.textSecondary }}>
                {field.helpText}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Inline settings panel */}
        {settingsExpanded && (
          <Box
            sx={{
              borderTop: `1px solid ${palette.border}`,
              backgroundColor: palette.surfaceSubtle,
              px: 1.25,
              py: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <SettingsInput
              label="Field key"
              value={field.key}
              onChange={(val) => onUpdate(index, { key: val.replace(/\s+/g, '_').toLowerCase() })}
              placeholder="field_key"
              mono
              palette={palette}
              theme={theme}
            />
            {!isDisplayField && (
              <>
                <SettingsInput
                  label="Help text"
                  value={field.helpText || ''}
                  onChange={(val) => onUpdate(index, { helpText: val })}
                  placeholder="Optional help text"
                  palette={palette}
                  theme={theme}
                />
                {['text', 'number', 'date', 'time'].includes(field.type) && (
                  <SettingsInput
                    label="Prefill value"
                    value={field.defaultValue || ''}
                    onChange={(val) => onUpdate(index, { defaultValue: val })}
                    placeholder={
                      field.type === 'date'
                        ? 'YYYY-MM-DD'
                        : field.type === 'time'
                          ? 'HH:mm'
                          : 'Default answer (optional)'
                    }
                    type={field.type === 'number' ? 'number' : 'text'}
                    palette={palette}
                    theme={theme}
                  />
                )}
                <SettingsToggle
                  label="Required"
                  checked={field.required || false}
                  onChange={(val) => onUpdate(index, { required: val })}
                  palette={palette}
                />
              </>
            )}
            {field.type === 'text' && (
              <SettingsToggle
                label="Multiline"
                checked={field.multiline || false}
                onChange={(val) => onUpdate(index, { multiline: val })}
                palette={palette}
              />
            )}

            {field.type === 'file_upload' && (
              <FileUploadSettings field={field} onUpdate={(updates) => onUpdate(index, updates)} palette={palette} theme={theme} />
            )}

            {/* Validation rules */}
            <ValidationSection
              field={field}
              validations={validations}
              onValidationsChange={handleValidationsChange}
              palette={palette}
              theme={theme}
            />
          </Box>
        )}
      </Box>

      {/* Bottom toolbar */}
      <Box
        className="block-bottom-bar"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2px',
          py: '2px',
          opacity: showControls ? 1 : 0,
          height: showControls ? '24px' : 0,
          overflow: 'hidden',
          transition: 'opacity 0.15s ease, height 0.15s ease',
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setSettingsExpanded((v) => !v); }}
          sx={{
            p: '3px',
            color: settingsExpanded ? palette.textPrimary : palette.textSecondary,
            backgroundColor: settingsExpanded ? palette.border : 'transparent',
            borderRadius: '4px',
          }}
          title="Settings"
        >
          <SettingsIcon sx={{ width: 15, height: 15 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setAddMenuAnchor(e.currentTarget); }}
          sx={{ p: '3px', color: palette.textSecondary }}
          title="Add field below"
        >
          <AddIcon sx={{ width: 15, height: 15 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onDuplicate(index); }}
          sx={{ p: '3px', color: palette.textSecondary }}
          title="Duplicate"
        >
          <ContentCopyIcon sx={{ width: 14, height: 14 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onRemove(index); }}
          sx={{ p: '3px', color: palette.textSecondary, '&:hover': { color: palette.errorText || '#b91c1c' } }}
          title="Delete"
        >
          <DeleteOutlineIcon sx={{ width: 15, height: 15 }} />
        </IconButton>
      </Box>

      {/* Add field menu */}
      <Menu
        anchorEl={addMenuAnchor}
        open={addMenuOpen}
        onClose={() => setAddMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 240,
              maxHeight: 360,
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              mt: '4px',
            },
          },
        }}
      >
        {FIELD_CATEGORIES.map((cat, catIdx) => [
          catIdx > 0 && <Divider key={`div-${catIdx}`} />,
          <Typography
            key={`cat-${cat.label}`}
            sx={{
              px: '16px',
              pt: catIdx === 0 ? '8px' : '6px',
              pb: '4px',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
              userSelect: 'none',
            }}
          >
            {cat.label}
          </Typography>,
          ...cat.types.map((t) => {
            const Icon = t.icon;
            return (
              <MenuItem
                key={t.value}
                onClick={() => handleAddSelect(t.value)}
                sx={{
                  px: '16px',
                  py: '6px',
                  minHeight: 0,
                  mx: '4px',
                  borderRadius: '4px',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <ListItemIcon sx={{ minWidth: '28px !important' }}>
                  <Icon sx={{ width: 16, height: 16 }} />
                </ListItemIcon>
                <ListItemText
                  primary={t.label}
                  primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }}
                />
              </MenuItem>
            );
          }),
        ])}
      </Menu>
    </Box>
  );
}
